// The Coast look for rendered markdown, shared with every other markdown surface in the app.
// Its cascade layer puts it over the library's own stylesheet wherever it lands in the load order.
import './markdownTheme.css';
import MarkdownPreview from '@uiw/react-markdown-preview/nohighlight';
import { clsx } from 'clsx';
import { Element, Nodes } from 'hast';
import { CSSProperties, ComponentProps, ReactNode, useMemo } from 'react';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { Link } from '@shared/components/link';
import { FormattedTokenText } from '../FormattedText/FormattedTokenText';
import {
  PLACEHOLDER_CODE_BASE,
  encodeTokenPlaceholders,
} from '../FormattedText/encodeTokenPlaceholders';
import { FormattedTextToken } from '../FormattedText/props';
import { CopyButton } from './CopyButton';
import { truncateLinkLabel } from './linkLabel';
import { rehypeBreakNewlines } from './rehypeBreakNewlines';
import { TOKEN_PROPERTY, rehypeTokenPlaceholders } from './rehypeTokenPlaceholders';
import { remarkLinkPlaceholders } from './remarkLinkPlaceholders';
import { remarkLineBreaks, remarkLiteralHtml } from './remarkLiteralHtml';

export type MarkdownComponents = ComponentProps<typeof MarkdownPreview>['components'];
type MarkdownRehypePlugins = ComponentProps<typeof MarkdownPreview>['rehypePlugins'];
type MarkdownRemarkPlugins = ComponentProps<typeof MarkdownPreview>['remarkPlugins'];
type RehypeRewrite = ComponentProps<typeof MarkdownPreview>['rehypeRewrite'];

export interface MarkdownContentProps {
  /** The markdown to render. */
  source: string;

  /**
   * Mentions, hashtags, links and emails the backend found in `source`. Passing none renders
   * plain markdown, which is what a form instruction is.
   */
  tokens?: FormattedTextToken[];

  /** The colour a token is drawn in — a mention, a hashtag, a link. */
  highlightColor?: string;

  /** The colour the prose is drawn in. Everything the theme derives resolves against it. */
  color?: string;

  /** Per-mention override, eg to tint only the reader's own mention. */
  mentionColor?: (token: FormattedTextToken) => string | undefined;

  /** Offers a copy button over code blocks and tables. */
  copyable?: boolean;

  /**
   * Renders every link as a plain anchor opening in a new tab, rather than through the app's
   * own `Link`. Set by surfaces outside the app — a public form has no router to hand a path to.
   */
  externalLinks?: boolean;

  /**
   * Renderers that take over from the defaults tag by tag, for a surface that knows more about
   * what its markdown means — Ask draws a table of cards, and a link to one, as the app's own
   * card UI. An override can fall back to the default by rendering the export it replaces
   * (`MarkdownLink`, `MarkdownTable`).
   */
  components?: MarkdownComponents;

  className?: string;
}

const EMPTY_TOKENS: FormattedTextToken[] = [];

/**
 * Markdown, rendered the one way the app renders markdown.
 *
 * A chat message, an Ask answer and a form instruction all come through here, so what someone
 * types in one place reads the same in every other. The library that renders it is the same one
 * that edits it in the instruction composer and the chat input, which is what keeps writing and
 * reading from drifting apart.
 *
 * Chat's tokens are what this adds over rendering markdown plainly: the mentions, hashtags and
 * URLs the backend found, drawn as the app draws them everywhere else rather than as whatever
 * the markdown parser made of the characters.
 */
export function MarkdownContent({
  source,
  tokens = EMPTY_TOKENS,
  highlightColor,
  color,
  mentionColor,
  copyable = false,
  externalLinks = false,
  components: overrides,
  className,
}: MarkdownContentProps) {
  const { encoded, tokenByCharCode } = useMemo(
    () => encodeTokenPlaceholders(source, tokens),
    [source, tokens],
  );

  const components = useMemo<MarkdownComponents>(
    () => ({
      // Inline media has its own layout item in a form and its own attachment in a message, so
      // it does not belong in prose.
      img: () => null,
      input: TaskCheckbox,
      a: externalLinks ? ExternalMarkdownLink : MarkdownLink,
      span: props => (
        <TokenSpan
          {...props}
          tokenByCharCode={tokenByCharCode}
          highlightColor={highlightColor}
          mentionColor={mentionColor}
        />
      ),
      // A table always needs its wrapper — that is what scrolls it and what lets it lay its
      // columns out across the column it is in. The copy button is the only optional part.
      table: copyable ? CopyableTable : TableBlock,
      ...(copyable ? { pre: CopyablePre } : {}),
      ...overrides,
    }),
    [tokenByCharCode, highlightColor, mentionColor, copyable, externalLinks, overrides],
  );

  /*
   * The surface says what colour it is and the markdown follows: `markdownTheme.css` derives
   * every border, every muted shade and every code background from `currentColor` and
   * `--theme-background`, so one stylesheet covers a white form and a purple bubble alike.
   */
  const style = useMemo(
    () => ({ ...MARKDOWN_STYLE, color, '--markdown-accent': highlightColor }) as CSSProperties,
    [color, highlightColor],
  );

  // HTML stays as the text it was typed as, a single newline stays a line break, and a link goes
  // where it was written to go rather than to the placeholder its URL was swapped for.
  const remarkPlugins = useMemo<MarkdownRemarkPlugins>(
    () => [remarkLiteralHtml, remarkLineBreaks, [remarkLinkPlaceholders, { tokenByCharCode }]],
    [tokenByCharCode],
  );

  const rehypePlugins = useMemo<MarkdownRehypePlugins>(
    () => [
      rehypeBreakNewlines,
      [rehypeTokenPlaceholders, { tokenByCharCode }],
      [rehypeSanitize, SANITIZE_SCHEMA],
    ],
    [tokenByCharCode],
  );

  return (
    <MarkdownPreview
      source={encoded}
      className={clsx(MARKDOWN_CLASS_NAME, className)}
      components={components}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      rehypeRewrite={stripHeadingAnchors}
      // The library pushes a copy button into every `pre`. Sanitising strips its class and its
      // payload and leaves the empty div behind, so all it ever did here was sit inside the
      // element the code block now scrolls on. `copyable` offers our own instead.
      disableCopy
      style={style}
    />
  );
}

/**
 * The class `markdownTheme.css` styles. It is ours rather than the library's own
 * `.wmde-markdown`, so the theme never depends on a name the library is free to change.
 */
const MARKDOWN_CLASS_NAME = 'markdown-content';

const MARKDOWN_STYLE = { background: 'transparent' };

/**
 * Sanitises the rendered HTML.
 *
 * `components` is a rendering map, not a filter — it decides what React draws for a tag markdown
 * produced, and says nothing about an attribute typed straight into the source. This is read by
 * everyone in a workspace, and on a public form by people outside it entirely.
 *
 * GitHub's own schema is the default and covers what markdown here needs. The one addition is
 * the attribute a token span carries, which the schema would otherwise strip on its way past —
 * taking every mention's colour and every link's behaviour with it.
 */
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span ?? []), TOKEN_PROPERTY],
    // GitHub's schema admits a checkbox and its `disabled`, but not `checked` — which is the one
    // thing a checklist is written to say. Letting it through is safe precisely because the box
    // is inert: an attribute on a control nobody can operate decides nothing but what is drawn.
    input: [...(defaultSchema.attributes?.input ?? []), 'checked'],
  },
};

export type MarkdownNodeProps = { node?: Element };

/**
 * A checklist box: it shows a state, and it does not offer to change one.
 *
 * Nothing here stores a tick. A form has fields for that, and a message is a record of what was
 * said rather than a surface to work on — so a box that answered a click would either forget the
 * answer or quietly rewrite what somebody wrote. The list is edited where it was written, in the
 * markdown, and read everywhere else.
 *
 * `disabled` rather than a click handler that declines: it is what actually stops the box being
 * operated, what takes it out of the tab order, and what a screen reader reads out to explain
 * why. `type` and `checked` are pinned because this is the only input markdown can produce, and
 * the state it shows has to be the state that was typed.
 */
function TaskCheckbox({
  node: _node,
  checked,
  ...props
}: MarkdownNodeProps & ComponentProps<'input'>) {
  return <input {...props} type="checkbox" checked={!!checked} disabled />;
}

/**
 * One mention, hashtag, link or email, drawn the way the app draws it everywhere else.
 *
 * Any other span is markdown's own and passes through untouched.
 */
function TokenSpan({
  node,
  children,
  tokenByCharCode,
  highlightColor,
  mentionColor,
  ...props
}: MarkdownNodeProps &
  ComponentProps<'span'> & {
    tokenByCharCode: Map<number, FormattedTextToken>;
    highlightColor?: string;
    mentionColor?: (token: FormattedTextToken) => string | undefined;
  }) {
  const index = node?.properties?.[TOKEN_PROPERTY];
  const token =
    typeof index === 'string'
      ? tokenByCharCode.get(PLACEHOLDER_CODE_BASE + Number(index))
      : undefined;

  if (!token) {
    return <span {...props}>{children}</span>;
  }

  return (
    <FormattedTokenText token={token} highlightColor={highlightColor} mentionColor={mentionColor}>
      {token.value}
    </FormattedTokenText>
  );
}

/**
 * A link in a message or an answer, opening the way every other link in the app opens: an
 * in-app path through the router, anything else in a new tab. `Link` is what decides which.
 */
export function MarkdownLink({
  node,
  children,
  href,
  ...props
}: MarkdownNodeProps & ComponentProps<'a'>) {
  if (!href) {
    return <a {...props}>{children}</a>;
  }

  // `react-i18next` widens `children` globally to `ReactI18NextChildren`, which includes a bare
  // record and so is not a `ReactNode`. JSX takes it either way; a plain function has to be told
  // which of the two it is getting.
  return <Link href={href}>{linkLabel(node, href, children as ReactNode)}</Link>;
}

/**
 * What a link shows, which is not always what it links to.
 *
 * `[](/cards/1)` has nothing to show but the target itself. A label that is a long URL — which is
 * what the Ask agent produces when it links to a filtered view — is middle-truncated, so a link
 * cannot push a message wider than the column it sits in while its `href` stays whole.
 *
 * Anything a person wrote is left exactly as they wrote it: only a label that *is* its own href
 * is truncated, since that is the one nobody chose the length of.
 */
function linkLabel(node: Element | undefined, href: string, children: ReactNode): ReactNode {
  const text = node ? nodeText(node) : '';

  if (!text) {
    return href;
  }

  if (text === href) {
    return truncateLinkLabel(text);
  }

  return children;
}

/**
 * A link in a form, opening in a new tab.
 *
 * The form is one someone is part-way through filling in, and following a link in place would
 * take the answers with it — an external form holds them nowhere but the page. `rel` goes with
 * the target: it denies the opened page a handle on the form it came from.
 */
function ExternalMarkdownLink({
  node: _node,
  children,
  ...props
}: MarkdownNodeProps & ComponentProps<'a'>) {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/**
 * A code block or a table with a copy button over its top right corner.
 *
 * Both are things people came to take away rather than to read in place — a command to run, a
 * few rows to paste into a sheet — and both are the one part of a message that selecting by hand
 * does badly, since either may be scrolled sideways past the edge of what is on screen.
 */
function CopyablePre({ node, ...props }: MarkdownNodeProps & ComponentProps<'pre'>) {
  return (
    <CopyableBlock node={node}>
      <pre {...props} />
    </CopyableBlock>
  );
}

function CopyableTable({ node, ...props }: MarkdownNodeProps & ComponentProps<'table'>) {
  return (
    <CopyableBlock node={node} data-markdown-table>
      <table {...props} />
    </CopyableBlock>
  );
}

/** The table a surface gets by default, with its copy button or without. */
export function MarkdownTable({
  copyable,
  ...props
}: MarkdownNodeProps & ComponentProps<'table'> & { copyable: boolean }) {
  return copyable ? <CopyableTable {...props} /> : <TableBlock {...props} />;
}

/** The same box, without a copy button — a form reads a table rather than taking it away. */
function TableBlock({ node: _node, ...props }: MarkdownNodeProps & ComponentProps<'table'>) {
  return (
    <div data-markdown-table>
      <table {...props} />
    </div>
  );
}

function CopyableBlock({
  node,
  children,
  ...props
}: MarkdownNodeProps & { children: ReactNode } & ComponentProps<'div'>) {
  return (
    <div {...props} data-copyable>
      {children}
      <div data-copy-button>
        <CopyButton text={node ? nodeText(node) : ''} />
      </div>
    </div>
  );
}

/** Everything a node holds, as the plain text it would copy as. */
function nodeText(node: Nodes): string {
  if (node.type === 'text') {
    return node.value;
  }
  if (!('children' in node)) {
    return '';
  }
  return node.children.map(nodeText).join('');
}

/**
 * Drops the link the preview hangs off every heading.
 *
 * `rehype-autolink-headings` is baked into the library's own plugin list with no prop to turn it
 * off, so the anchor is removed after the fact — this hook runs once the library's own rewrite
 * has already inserted it. A heading in a message or a form is a heading, with nowhere for a
 * deep link to point.
 */
const stripHeadingAnchors: RehypeRewrite = (node, _index, parent) => {
  if (node.type !== 'element' || parent?.type !== 'root' || !/^h[1-6]$/.test(node.tagName)) {
    return;
  }

  node.children = node.children.filter(
    child => !(child.type === 'element' && child.properties?.ariaHidden === 'true'),
  );
};
